import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiDownloadCloud, FiX, FiPlus, FiSlash, FiCopy, FiCheck } from 'react-icons/fi';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const emptyForm = () => ({ entity: '', period_start: '', period_end: '', auditor_name: '', expires_at: '', mfa_code: '' });

const AuditorExports = () => {
  const [entities, setEntities] = useState([]);
  const [exportsList, setExportsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, expRes] = await Promise.all([financeAPI.entities.list(), financeAPI.auditorExports.list()]);
      setEntities(entRes.data.results || entRes.data || []);
      setExportsList(expRes.data.results || expRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load auditor exports.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const { mfa_code, ...data } = form;
      await financeAPI.auditorExports.create({ ...data, entity: data.entity || null }, mfa_code);
      setForm(emptyForm());
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to create export — check your MFA code.');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (exp) => {
    if (!window.confirm('Revoke this export link? It will stop working immediately.')) return;
    setBusyId(exp.id);
    setError(null);
    try { await financeAPI.auditorExports.revoke(exp.id); load(); }
    catch (e) { setError(e?.response?.data?.error || 'Failed to revoke.'); }
    finally { setBusyId(null); }
  };

  const copyLink = (exp) => {
    navigator.clipboard?.writeText(exp.download_url);
    setCopiedId(exp.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const entityName = (id) => id ? (entities.find((e) => e.id === id)?.name || '—') : 'Consolidated (both entities)';

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiDownloadCloud size={22} />Auditor Exports</h1>
          <p style={fs.pageSubtitle}>Read-only, watermarked, time-boxed PDF — trial balance, general ledger, supporting documents. No login required to download.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Export</button>
          <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} style={{ ...fs.card, marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div><div style={fs.label}>Entity</div>
            <select value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Consolidated (both)</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Auditor Name</div><input required value={form.auditor_name} onChange={(e) => setForm({ ...form, auditor_name: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div><div style={fs.label}>Period Start</div><input required type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div><div style={fs.label}>Period End</div><input required type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div><div style={fs.label}>Access Expires</div><input required type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div><div style={fs.label}>Your MFA Code (high-risk action)</div><input required maxLength={10} value={form.mfa_code} onChange={(e) => setForm({ ...form, mfa_code: e.target.value })} placeholder="000000" style={{ ...fs.input, width: '100%' }} /></div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Creating…' : 'Create Export'}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : exportsList.length === 0 ? (
        <div style={fs.emptyState}><FiDownloadCloud size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No auditor exports yet.</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {exportsList.map((exp) => {
            const busy = busyId === exp.id;
            const status = exp.revoked_at ? 'REVOKED' : exp.is_active ? 'ACTIVE' : 'EXPIRED';
            const color = status === 'ACTIVE' ? fs.colors.green : status === 'REVOKED' ? fs.colors.red : '#888';
            return (
              <div key={exp.id} style={fs.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ color: fs.colors.textBright, fontWeight: 600, fontSize: 14 }}>{exp.auditor_name}</span>
                      <span style={fs.badge(color)}>{status}</span>
                    </div>
                    <div style={{ color: fs.colors.textFaint, fontSize: 12, marginTop: 4 }}>
                      {entityName(exp.entity)} · {exp.period_start} → {exp.period_end} · expires {new Date(exp.expires_at).toLocaleString()}
                    </div>
                    <div style={{ color: fs.colors.textFaint, fontSize: 11, marginTop: 4 }}>{exp.download_count} download(s)</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {status === 'ACTIVE' && (
                      <>
                        <button onClick={() => copyLink(exp)} style={fs.neutralButton}>
                          {copiedId === exp.id ? <FiCheck size={12} style={{ marginRight: 4, verticalAlign: -1 }} /> : <FiCopy size={12} style={{ marginRight: 4, verticalAlign: -1 }} />}
                          {copiedId === exp.id ? 'Copied' : 'Copy Link'}
                        </button>
                        <button onClick={() => revoke(exp)} disabled={busy} style={fs.dangerButton}><FiSlash size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Revoke</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AuditorExports;
